import { usePlayground } from "@/components/Playground2/playground-store";
import TemplateSelectDropdown from "@/components/TemplateSelectDropdown";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import PromptVersionSelector from "@/components/ui/PromptVersionSelector";
import { useUserToken } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { ToastType } from "@/enums";
import { usePrompt } from "@/queries";

import { ListPromptVersion, PromptBlueprint } from "@/types";
import { displayErrorToast, displayToast } from "@/utils/toast";
import { ImportIcon } from "lucide-react";
import { useEffect, useState } from "react";

const ImportPromptButton = () => {
  const [selectedTemplate, setSelectedTemplate] = useState<{
    id: number | null;
    name: string | undefined;
  }>({ id: null, name: undefined });
  const [selectedVersion, setSelectedVersion] =
    useState<ListPromptVersion | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const userToken = useUserToken();
  const workspaceId = useUser()?.activeWorkspaceId!;
  const { data, isLoading } = usePrompt(userToken, {
    workspace_id: workspaceId,
    prompt_name: selectedTemplate.name,
    number: selectedVersion?.number,
  });

  const playground = usePlayground();

  useEffect(() => {
    if (!isOpen) {
      setSelectedTemplate({ id: null, name: undefined });
      setSelectedVersion(null);
    }
  }, [isOpen]);

  const handleTemplateSelection = ({
    id,
    name,
  }: {
    id: number;
    name: string;
  }) => {
    setSelectedTemplate({ id, name });
    setSelectedVersion(null);
  };

  const handleVersionChange = (version: ListPromptVersion) => {
    setSelectedVersion(version);
  };

  const handleImport = async () => {
    if (!selectedTemplate.id || !selectedVersion) {
      displayToast(
        "Please select both a template and a version",
        ToastType.error,
      );
      return;
    }

    playground.applyPromptBlueprintToState(data as Partial<PromptBlueprint>);

    try {
      displayToast(
        `Imported ${selectedTemplate.name} (Version #${selectedVersion.number})`,
        ToastType.success,
      );
      setIsOpen(false);
    } catch (error) {
      displayErrorToast(
        error instanceof Error ? error.message : "An unknown error occurred",
      );
    } finally {
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={`rounded-full transition-colors ${
            isOpen ? "!bg-gray-100" : ""
          }`}
        >
          <ImportIcon className="mr-2 h-4 w-4" />
          Load Prompt
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="border-primary-100 w-[350px] p-4 shadow-lg"
        align="start"
      >
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Select Prompt Template
            </label>
            <TemplateSelectDropdown
              selectedTemplateName={selectedTemplate.name}
              selectedTemplateId={selectedTemplate.id ?? undefined}
              handleTemplateSelection={handleTemplateSelection}
            />
          </div>

          {selectedTemplate.id ? (
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                Select Version
              </label>
              <PromptVersionSelector
                promptId={selectedTemplate.id}
                selectedVersion={selectedVersion}
                onValueChange={handleVersionChange}
                showLabel={false}
              />
            </div>
          ) : null}

          <div className="flex items-center justify-between">
            <Button
              size="sm"
              className="ml-auto flex w-full items-center gap-2 transition-all"
              onClick={handleImport}
              disabled={!selectedTemplate.id || !selectedVersion || isLoading}
            >
              {selectedTemplate && selectedVersion && isLoading ? (
                <>Loading...</>
              ) : (
                <>Load Prompt</>
              )}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default ImportPromptButton;
